import cn from "classnames";
import MagnifyingGlassIcon from "@atlas/uicorn/lib/Icon/MagnifyingGlassIcon";
import styles from "../styles/search.module.css";
import IconButton from "@atlas/uicorn/lib/Control/IconButton";
import CrossIcon from "@atlas/uicorn/lib/Icon/CrossIcon";
import useRefCallback from "use-ref-callback";
import React, { useCallback, useMemo, useRef } from "react";
import { AppSettings } from "../lib/get-app-settings";
import SearchResults, {
  TSearchResult,
  TSearchResultsRef,
} from "./search-results";
import { debounce, memoize, isEmpty as _isEmpty } from "lodash-es";
import trackSessionEvent, {
  getSessionDetailsFromLocalStorage,
} from "../lib/hc-session";
import searchArticles, { Article } from "../lib/search-articles";
import { useRouter } from "next/router";
import loadCategories, { Section } from "../lib/load-categories";
import formatDate from "../lib/format-date";
import t from "../texts/t";
import useRequest from "use-request";
import loadTrendingArticles from "../lib/load-trending-articles";
import useUrlWithPreview from "../lib/use-url-with-preview";
import { searchHotkey } from "./layout-sidebar.light";

export default function Search(props: TSearchProps) {
  const {
    className,
    appSettings,
    baseUrl,
    defaultValue,
    inputRef,
    collapsed,
    onClose,
    onFocus,
    onBlur,
    onKeyDown,
  } = props;

  const companySlug = appSettings.slug;

  const pageUrl = useRouter().asPath;

  const resultsRef = useRef<TSearchResultsRef>(null);

  const getUrlWithPreview = useUrlWithPreview();

  const handleKeyDown = useRefCallback<
    React.KeyboardEventHandler<HTMLDivElement>
  >((event) => {
    if (resultsRef.current) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (!resultsRef.current.next()) inputRef.current?.focus();
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        if (!resultsRef.current.prev()) inputRef.current?.focus();
        return;
      }
    }

    onKeyDown?.(event);
  });

  const handleSubmit = useRefCallback<React.FormEventHandler<HTMLFormElement>>(
    (event) => event.preventDefault()
  );

  const categoriesMapPromiseRef = useRef<Promise<
    Record<string, Section>
  > | null>(null);

  const requestCategoriesMap = useRefCallback(() => {
    if (!categoriesMapPromiseRef.current) {
      categoriesMapPromiseRef.current = loadCategories(
        companySlug,
        undefined,
        appSettings.appId
      ).then((categories = []) =>
        categories.reduce<Record<string, Section>>(
          (map, category) => Object.assign(map, { [category.id]: category }),
          {}
        )
      );
    }
    return categoriesMapPromiseRef.current;
  });

  const transformWithPreview = useCallback(
    (
      map: Record<string, Section>,
      baseUrl: string,
      language: string | undefined,
      query: string,
      article: Article
    ): TSearchResult => {
      const category = map[article.categoryId];
      const _url = _isEmpty(query)
        ? getUrlWithPreview(`${baseUrl}/articles/${article.slug}`)
        : getUrlWithPreview(
            `${baseUrl}/articles/${article.slug}?SEARCHED_EVENT=${query}`
          );
      return {
        url: _url,
        name: article.title,
        categoryName: category.title,
        parentCategoryName:
          (category.parentId && map[category.parentId].title) || "",
        icon: category.icon,
        publishDate: formatDate(article.lastPublishedAt, language),
      };
    },
    [getUrlWithPreview]
  );

  const memoizedSearch = useMemo<(query: string) => Promise<TSearchResult[]>>(
    () =>
      memoize((query: string) => {
        const hcSessionDetails = getSessionDetailsFromLocalStorage(appSettings);
        return Promise.all([
          requestCategoriesMap(),
          searchArticles(
            companySlug,
            query,
            "",
            10,
            0,
            undefined,
            appSettings.appId
          ),
        ]).then(([map, results]) => {
          if (hcSessionDetails) {
            const _event = results.length ? "SEARCHED" : "SEARCHED_NO_RESULTS";
            trackSessionEvent(_event, hcSessionDetails, pageUrl, results, [], {
              query,
            });
          }

          return results.map((article) =>
            transformWithPreview(
              map,
              baseUrl,
              appSettings.language,
              query,
              article
            )
          );
        });
      }),
    [
      appSettings,
      requestCategoriesMap,
      companySlug,
      pageUrl,
      transformWithPreview,
      baseUrl,
    ]
  );

  const trendingRequest = useRequest<TSearchResult[]>(() =>
    Promise.all([
      requestCategoriesMap(),
      loadTrendingArticles(companySlug, undefined, appSettings.appId),
    ]).then(([map, results]) =>
      results.map((article) =>
        transformWithPreview(map, baseUrl, appSettings.language, "", article)
      )
    )
  );

  const handleFocus = useRefCallback(function initiateTrendingRequest() {
    if (trendingRequest.idle) trendingRequest.execute();
  });

  const { value: searchResults, execute: searchCallback } =
    useRequest(memoizedSearch);

  const handleChange = useMemo(
    () =>
      debounce(() => {
        if (inputRef.current) searchCallback(inputRef.current.value);
      }, 700),
    [inputRef, searchCallback]
  );

  const handleScroll = useRefCallback<React.UIEventHandler<HTMLDivElement>>(
    (event) => {
      if (event.currentTarget.scrollTop) {
        event.currentTarget.classList.add(styles.modalScrolled);
      } else {
        event.currentTarget.classList.remove(styles.modalScrolled);
      }
    }
  );

  const handleClose = useRefCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >((event) => {
    event.preventDefault();
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    if (
      document.activeElement &&
      "blur" in document.activeElement &&
      typeof document.activeElement.blur === "function"
    ) {
      document.activeElement.blur();
    }
    onClose();
  });

  const results = collapsed
    ? undefined
    : inputRef.current?.value
    ? searchResults
    : trendingRequest.value;

  return (
    <div
      className={cn(styles.searchContainer, className)}
      onScroll={handleScroll}
      onKeyDown={handleKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      tabIndex={0}
    >
      <form
        className={styles.searchForm}
        onSubmit={handleSubmit}
        itemProp="potentialAction"
        itemScope
        itemType="http://schema.org/SearchAction"
      >
        <meta
          itemProp="target"
          content={`${baseUrl}/search?q={search_term_string}`}
        />
        <meta
          itemProp="query-input"
          content="required name=search_term_string"
        />
        <MagnifyingGlassIcon />
        <input
          type="search"
          placeholder={t("Search for articles...", appSettings.language)}
          onChange={handleChange}
          onFocus={handleFocus}
          ref={inputRef}
          lang={appSettings.language}
          defaultValue={defaultValue}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
        />
        <kbd className={styles.menuSearchKey}>
          <big suppressHydrationWarning>
            {searchHotkey.toString().slice(0, -1)}
          </big>
          <span>{searchHotkey.toString().slice(-1)}</span>
        </kbd>
        <IconButton
          className={styles.menuSearchCloseButton}
          tooltip="Clear"
          icon={<CrossIcon />}
          noHoverStyle
          onClick={handleClose}
        />
      </form>

      <SearchResults
        ref={resultsRef}
        visible={!!results}
        results={results || []}
        language={appSettings.language}
        onClose={onClose}
      />
    </div>
  );
}

type TSearchProps = {
  className?: string;
  appSettings: AppSettings;
  baseUrl: string;
  defaultValue?: string;
  inputRef: React.RefObject<HTMLInputElement>;
  collapsed?: boolean;
  onClose: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};
